<template>
  <div class="report-page department-collection-report">
    <ReportViewBar
      title="Collection by Department"
      :start="start"
      :end="end"
    />

    <v-container fluid>
      <div class="report-page__intro no-print">
        <div>
          <div class="report-page__eyebrow">School revenue</div>
          <div class="headline font-weight-bold">Department collection</div>
          <div class="report-page__description">
            Compare fee charges with payments allocated to each class or department.
          </div>
        </div>
        <div class="report-period">
          <v-icon small color="primary">mdi-calendar-range</v-icon>
          <span>{{ formattedPeriod }}</span>
        </div>
      </div>

      <v-alert v-if="error" type="error" outlined class="mb-5">
        {{ error }}
        <v-btn text small color="error" class="ml-2" @click="loadReport">Retry</v-btn>
      </v-alert>

      <template v-if="isLoading">
        <v-progress-linear indeterminate color="primary" class="mb-4" />
        <v-skeleton-loader type="card, table" />
      </template>

      <template v-else-if="!error">
        <v-row class="mb-2">
          <v-col cols="12" sm="6" lg="3">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Amount invoiced</div>
                <div class="report-kpi__value">{{ totals.invoiced | currency }}</div>
                <div class="report-kpi__hint">From invoices created in this period</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Amount collected</div>
                <div class="report-kpi__value primary--text">{{ totals.collected | currency }}</div>
                <div class="report-kpi__hint">Payments received in this period</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Charges created</div>
                <div class="report-kpi__value">{{ totals.chargeCount }}</div>
                <div class="report-kpi__hint">Invoice detail rows created in this period</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Collection rate</div>
                <div class="report-kpi__value">{{ totals.collectionRate }}%</div>
                <div class="report-kpi__hint">Collected amount divided by invoiced amount</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card flat class="report-section mb-5">
          <div class="report-section__header">
            <div>
              <div class="report-section__title">Amount collected by department</div>
              <div class="report-section__subtitle">Money received during the selected period</div>
            </div>
            <v-chip small outlined color="primary">{{ rows.length }} departments</v-chip>
          </div>
          <div v-show="rows.length" ref="chartContainer" class="department-collection-report__chart" />
          <div v-if="!rows.length" class="report-empty">
            <v-icon large color="grey lighten-1">mdi-chart-box-outline</v-icon>
            <div class="mt-2 font-weight-medium">No school collections found</div>
            <div>Try another reporting period.</div>
          </div>
        </v-card>

        <v-card flat class="report-section" v-if="rows.length">
          <div class="report-section__header">
            <div>
              <div class="report-section__title">Department details</div>
              <div class="report-section__subtitle">Fee charge and collection totals for the selected period</div>
            </div>
          </div>
          <v-data-table
            :headers="headers"
            :items="rows"
            :items-per-page="rows.length"
            sort-by="collectedAmount"
            :sort-desc="true"
            hide-default-footer
            class="report-table"
          >
            <template v-slot:item.departmentName="{ item }">
              <span class="font-weight-medium">{{ item.departmentName }}</span>
            </template>
            <template v-slot:item.invoicedAmount="{ item }">
              <strong class="report-amount">{{ item.invoicedAmount | currency }}</strong>
            </template>
            <template v-slot:item.collectedAmount="{ item }">
              <strong class="report-amount report-amount--primary">{{ item.collectedAmount | currency }}</strong>
            </template>
            <template v-slot:body.append>
              <tr class="report-table-total">
                <td>Total</td>
                <td class="text-right">{{ totals.chargeCount }}</td>
                <td class="text-right">{{ totals.invoiced | currency }}</td>
                <td class="text-right">{{ totals.collected | currency }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script>
import Highcharts from "highcharts";
import ReportViewBar from "@/components/ReportViewBar";
import paymentService from "@/modules/Payment/service";
import "@/modules/ReportView/report.css";

export default {
  components: { ReportViewBar },
  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      error: null,
      rows: [],
      headers: [
        { text: "DEPARTMENT", value: "departmentName", align: "left" },
        { text: "CHARGES CREATED", value: "chargeCount", align: "right" },
        { text: "AMOUNT INVOICED", value: "invoicedAmount", align: "right" },
        { text: "AMOUNT COLLECTED", value: "collectedAmount", align: "right" },
      ],
    };
  },
  computed: {
    formattedPeriod() {
      if (!this.start || !this.end) return "Selected period";
      return `${this.$moment(this.start).format("MMM D, YYYY")} – ${this.$moment(this.end).format("MMM D, YYYY")}`;
    },
    totals() {
      const totals = this.rows.reduce((result, row) => ({
        chargeCount: result.chargeCount + row.chargeCount,
        invoiced: result.invoiced + row.invoicedAmount,
        collected: result.collected + row.collectedAmount,
      }), { chargeCount: 0, invoiced: 0, collected: 0 });
      totals.collectionRate = totals.invoiced > 0
        ? ((totals.collected / totals.invoiced) * 100).toFixed(1)
        : "0.0";
      return totals;
    },
  },
  created() {
    this.start = this.$route.query.start;
    this.end = this.$route.query.end;
    this.loadReport();
  },
  methods: {
    number(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeRows(payload) {
      const source = Array.isArray(payload)
        ? payload
        : payload.departments || payload.rows || payload.data || [];
      return source.map((row, index) => {
        const invoicedAmount = this.number(row.invoicedAmount != null ? row.invoicedAmount : row.totalInvoiced);
        const collectedAmount = this.number(row.collectedAmount != null ? row.collectedAmount : row.totalCollected);
        return {
          departmentId: row.departmentId || row.classId || index,
          departmentName: row.departmentName || row.className || row.productName || "Unassigned",
          // Older API responses do not have chargeCount. Falling back to the
          // former invoice count keeps the report usable during a rolling deploy.
          chargeCount: this.number(row.chargeCount != null ? row.chargeCount : row.invoiceCount || row.totalInvoices),
          invoicedAmount,
          collectedAmount,
        };
      });
    },
    renderChart() {
      if (!this.$refs.chartContainer || !this.rows.length) return;
      const sorted = [...this.rows].sort((a, b) => b.collectedAmount - a.collectedAmount);
      Highcharts.chart(this.$refs.chartContainer, {
        credits: { enabled: false },
        chart: { type: "bar", backgroundColor: "transparent", height: Math.max(320, sorted.length * 48) },
        title: { text: null },
        xAxis: { categories: sorted.map(row => row.departmentName), title: { text: null } },
        yAxis: { min: 0, title: { text: "Amount" } },
        legend: { align: "center", verticalAlign: "bottom" },
        tooltip: { shared: true, valuePrefix: "$", valueDecimals: 2 },
        plotOptions: { series: { borderRadius: 3, groupPadding: 0.12 } },
        colors: [this.$vuetify.theme.currentTheme.primary],
        series: [
          { name: "Amount collected", data: sorted.map(row => row.collectedAmount) },
        ],
      });
    },
    async loadReport() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await paymentService.departmentCollectionSummary({ start: this.start, end: this.end });
        const payload = response && response.data != null ? response.data : response;
        this.rows = this.normalizeRows(payload || []);
        // The chart container is rendered only after loading finishes. Wait for
        // that DOM update before asking Highcharts to measure the container.
        this.isLoading = false;
        await this.$nextTick();
        this.renderChart();
      } catch (error) {
        this.error = "We couldn't load the department collection report.";
        console.error("Error loading department collection report", error);
      } finally {
        this.isLoading = false;
        this.$Progress.finish();
      }
    },
  },
};
</script>

<style scoped>
.department-collection-report__chart {
  min-height: 320px;
  padding: 8px 12px 16px;
}
</style>
